import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Space, Typography, message } from "antd";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { updateTransactionReceipt } from "../../api/transactionReceiptApi";
import { useTranslation } from "../../i18n/LanguageProvider";
import TransactionReceiptFormFields from "./TransactionReceiptFormFields";
import { buildTransactionReceiptPayload } from "./transactionReceiptUi";

const { Text } = Typography;

/**
 * Edit of one row, for the MANAGE roles. Uses the same field set as manual entry, pre-filled
 * from the record. The attached PDF is untouched by an edit.
 */
function TransactionReceiptEditModal({ open, record, onClose, onSaved }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !record) return;

        setError("");
        form.setFieldsValue({
            name: record.name || "",
            currency: record.currency || undefined,
            institution: record.institution || "",
            amount: record.amount === null || record.amount === undefined
                ? undefined
                : Number(record.amount),
            rate: record.rate === null || record.rate === undefined
                ? undefined
                : Number(record.rate),
            executionDate: record.executionDate ? dayjs(record.executionDate) : undefined,
        });
    }, [open, record, form]);

    const handleSubmit = async () => {
        setError("");

        try {
            const values = await form.validateFields();

            setSaving(true);

            const saved = await updateTransactionReceipt(
                record.id,
                buildTransactionReceiptPayload(values)
            );

            message.success(t("receipt.edit.saved"));
            onSaved?.(saved);
            onClose?.();
        } catch (err) {
            if (err?.errorFields) return;

            const text = err?.message || t("receipt.edit.saveFailed");
            setError(text);
            message.error(text);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <EditOutlined />
                    <span>{t("receipt.edit.modalTitle")}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={1000}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>{t("action.close")}</Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSubmit}
                    >
                        {t("action.save")}
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Text type="secondary">{t("receipt.edit.hint")}</Text>

                {error && (
                    <Alert type="error" showIcon message={t("edit.saveError")} description={error} />
                )}

                <Form form={form} layout="vertical" requiredMark autoComplete="off">
                    <TransactionReceiptFormFields t={t} />
                </Form>
            </Space>
        </Modal>
    );
}

export default TransactionReceiptEditModal;
